import React, { useState } from 'react';
import { css } from 'twin.macro';
import PropTypes from 'prop-types';
import remove from 'lodash/remove';
import Chip from '../Chip/Chip';
import { theme } from '../../../tailwind.config';
import CollapsibleListItem from '../CollapsibleListItem/CollapsibleListItem';
import { Body } from '../../styled components/Typography';
import Button from '../Button/Button';
import Table from '../Table';
import TableListItem from '../TableListItem';
import Checkbox from '../Checkbox/Checkbox';
import { removeDeletedOptionsFromCollectionToFilterOn } from '../../libs/ypQueryBuilder';

function FilterCategory({
  label,
  filterOptions = [],
  selectedOptions = [],
  handleChangeSelectedOptions,
  initiallyCollapsed = true,
  stickyPosition,
  noOptionsMessage = ''
}) {
  const filterOptionsWrapperRef = React.createRef();
  const filterCategoryStickyHeaderRef = React.createRef();
  const [collapsedCategory, setCollapsedCategory] = useState(initiallyCollapsed);

  const onFilterCategoryClick = () => {
    setCollapsedCategory(!collapsedCategory);
  };

  const toggleOptionInsideSelectedOptions = (item) => {
    const updatedCollection = selectedOptions;
    if (updatedCollection.some((id) => id === item.id)) {
      remove(updatedCollection, (id) => id === item.id);
    } else {
      updatedCollection.push(item.id);
    }
    handleChangeSelectedOptions(updatedCollection);
  };

  const renderCategoryRightElement = () => {
    const selectionWithoutDeletedOptions = removeDeletedOptionsFromCollectionToFilterOn(
      selectedOptions,
      filterOptions
    );
    const filtersActive = selectionWithoutDeletedOptions.length;
    const allOptionsSelected =
      selectionWithoutDeletedOptions.length === filterOptions.length;
    const disableToggleBtn = filterOptions.length === 0;
    const minWidthCounterNumber = '11px';
    const toggleAllBtnOnClick = (e) => {
      e.stopPropagation();
      if (allOptionsSelected) {
        handleChangeSelectedOptions([]);
      } else {
        const allOptions = filterOptions.map((option) => option.id);
        handleChangeSelectedOptions(allOptions);
      }
    };
    const toggleAllBtn = (
      <Button
        color={disableToggleBtn ? 'default' : 'primary'}
        tw="mr-4 "
        onClick={toggleAllBtnOnClick}
        dataAttributes={{
          'data-test-toggle-all': true
        }}
        disabled={disableToggleBtn}
      >
        {allOptionsSelected && !disableToggleBtn
          ? i18n('Deselect All')
          : i18n('Select All')}
      </Button>
    );

    return (
      <div tw="pr-12 flex items-center">
        {!collapsedCategory && toggleAllBtn}
        <Chip
          bgColor={
            filtersActive ? theme.colors.blue.base : theme.colors.silver['darken-30']
          }
          tw="my-2"
          variant="counter"
          dataAttributes={{
            'data-test-filter-category-counter': true,
            'data-test-bgcolor': filtersActive ? 'primary' : 'default'
          }}
        >
          <Body
            tw="inline-block "
            css={css`
              min-width: ${minWidthCounterNumber};
            `}
            $color="white"
          >
            {selectionWithoutDeletedOptions.length}
          </Body>
        </Chip>
      </div>
    );
  };

  const renderSwitchButton = (option) => {
    const selected = selectedOptions.some(
      (selectedOptionId) => selectedOptionId === option.id
    );
    return <Checkbox tw="py-4" variant="switch" defaultChecked={selected} />;
  };

  const renderNoLocationsMessage = () => (
    <div tw="py-8 px-48 text-center" data-test-no-options-message>
      <Body $color="gray dark">
        {noOptionsMessage || i18n('No filter options available for this category.')}
      </Body>
    </div>
  );

  const renderFilterOptions = () => (
    <div data-test-filter-category-options={label}>
      <Table>
        {filterOptions.map((option) => (
          <TableListItem
            label={option.name}
            disableKeyboardNavigation
            rightElement={renderSwitchButton(option)}
            onClick={() => toggleOptionInsideSelectedOptions(option)}
            key={`filter-editor-list-item-${option.name}-${option.id}`}
            dataAttributes={{ 'data-test-filteroption-id': option.id }}
          />
        ))}
      </Table>
    </div>
  );

  return (
    <div data-test-filter-category-wrapper={label}>
      <CollapsibleListItem
        label={label}
        rightElement={renderCategoryRightElement()}
        variant="secondary"
        onClick={(collapsed) => onFilterCategoryClick(collapsed)}
        initiallyCollapsed={initiallyCollapsed}
        dataAttributes={{
          'data-test-collapsible-filter-category': label
        }}
        stickyHeader
        stickyHeaderRef={filterCategoryStickyHeaderRef}
        stickyTop={stickyPosition}
        tw="bg-silver-lighten-30 "
      >
        <div tw="mb-24" ref={filterOptionsWrapperRef}>
          {filterOptions.length ? renderFilterOptions() : renderNoLocationsMessage()}
        </div>
      </CollapsibleListItem>
    </div>
  );
}

FilterCategory.propTypes = {
  label: PropTypes.string.isRequired,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({})),
  selectedOptions: PropTypes.arrayOf(PropTypes.string),
  handleChangeSelectedOptions: PropTypes.func.isRequired,
  initiallyCollapsed: PropTypes.bool,
  stickyPosition: PropTypes.number.isRequired,
  noOptionsMessage: PropTypes.string
};

export default FilterCategory;
